import type {EventListSummary} from '@/apiTypes/eventList';
import {CreateListModal} from '@/components/createListModal/createListModal';
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {Button, buttonVariants} from '@/components/ui/button';
import {Menu, MenuItem, MenuPopup, MenuTrigger} from '@/components/ui/menu';
import {Pill} from '@/components/ui/pill';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {toastManager} from '@/components/ui/toast';
import {Typography} from '@/components/ui/typography';
import {deleteEventList, getUserEventLists} from '@/server/savedEvent';
import {getFollowedLists, unfollowEventList} from '@/server/listFollower';
import {formatDate} from '@/utils/displayFormatUtils';
import {
  Link,
  createFileRoute,
  useRouter,
  useRouteContext,
  useNavigate,
} from '@tanstack/react-router';
import {Ellipsis} from 'lucide-react';
import {useState} from 'react';

export const Route = createFileRoute('/dashboard/lists/')({
  loader: async () => {
    const [lists, followedLists] = await Promise.all([
      getUserEventLists(),
      getFollowedLists(),
    ]);
    return {lists, followedLists};
  },
  component: ListsIndexPage,
});

/** Pill colors for list visibility. */
const visibilityBadgeConfig: Record<string, {color: string; label: string}> = {
  PRIVATE: {color: '#6b7280', label: 'Private'},
  PUBLIC: {color: '#16a34a', label: 'Public'},
};

/** Actions menu for a list row with delete confirmation. */
function ListActionsMenu({
  list,
  profileId,
}: {
  list: EventListSummary;
  profileId: string;
}) {
  const router = useRouter();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  async function handleDelete() {
    setLoading(true);
    try {
      await deleteEventList({data: list.id});
      toastManager.add({title: 'List deleted', type: 'success'});
      router.invalidate();
    } catch (err) {
      toastManager.add({
        title: err instanceof Error ? err.message : 'Failed to delete list',
        type: 'error',
      });
      setLoading(false);
      setDeleteOpen(false);
    }
  }

  return (
    <>
      <Menu>
        <MenuTrigger
          disabled={loading}
          aria-label="Open list actions"
          className={buttonVariants({variant: 'ghost', size: 'icon'})}
        >
          <Ellipsis className="w-4 h-4" />
        </MenuTrigger>
        <MenuPopup align="end">
          <MenuItem
            onClick={() => {
              navigate({
                to: '/dashboard/lists/$listId/edit',
                params: {listId: list.id},
              });
            }}
          >
            Edit
          </MenuItem>
          {list.visibility === 'PUBLIC' && (
            <MenuItem
              onClick={() => {
                navigate({
                  to: '/rider/$profileId/list/$listId',
                  params: {profileId, listId: list.id},
                  search: {page: 1, pageSize: 12},
                });
              }}
            >
              View public page
            </MenuItem>
          )}
          <MenuItem
            className="text-error-500 data-highlighted:text-error-500"
            onClick={() => setDeleteOpen(true)}
          >
            Delete
          </MenuItem>
        </MenuPopup>
      </Menu>
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete list</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{list.name}
              &rdquo;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              Cancel
            </AlertDialogClose>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={loading}
            >
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </>
  );
}

/**
 * Lists overview page in the profile dashboard.
 * Displays all event lists in a table with name, visibility, event count,
 * creation date, and actions.
 */
function ListsIndexPage() {
  const {lists, followedLists} = Route.useLoaderData();
  const [createOpen, setCreateOpen] = useState(false);
  const {profile} = useRouteContext({from: '__root__'});
  const {featureFlags} = useRouteContext({from: '__root__'});
  const advancedFiltersEnabled = featureFlags['advanced-filters'];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <Typography level="H3">Lists</Typography>
        <div className="flex items-center gap-2">
          {advancedFiltersEnabled && (
            <Link to="/dashboard/lists/new" search={{kind: 'smart'}}>
              <Button variant="outline">Create Smart List</Button>
            </Link>
          )}
          <Button onClick={() => setCreateOpen(true)}>Create List</Button>
        </div>
      </div>

      <div>
        <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="w-0">Visibility</TableHead>
                <TableHead className="w-0">Events</TableHead>
                <TableHead className="w-0">Followers</TableHead>
                <TableHead className="hidden lg:table-cell w-0">Created</TableHead>
                <TableHead className="w-0"><span className="sr-only">Actions</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lists.map((list) => {
                const badge = visibilityBadgeConfig[list.visibility];
                const isSavedEvents = list.name === 'Saved Events';
                return (
                  <TableRow key={list.id} className="group/row">
                    <TableCell>
                      <Link
                        to="/dashboard/lists/$listId"
                        params={{listId: list.id}}
                        className="hover:underline"
                      >
                        <Typography level="B2">{list.name}</Typography>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Pill size="xs" color={badge.color} className="w-fit">
                        {badge.label}
                      </Pill>
                    </TableCell>
                    <TableCell>
                      <Typography level="B3" className="text-gray-600">
                        {list.itemCount}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography level="B3" className="text-gray-600">
                        {list.visibility === 'PUBLIC'
                          ? list.followerCount
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <Typography level="B3" className="text-gray-600">
                        {formatDate(list.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell className="text-right">
                      {!isSavedEvents && profile && (
                        <div className="lg:opacity-0 lg:group-hover/row:opacity-100 transition-opacity">
                          <ListActionsMenu
                            list={list}
                            profileId={profile.id}
                          />
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
      </div>

      <div className="mt-10">
        <Typography level="H4" className="mb-4">Following</Typography>
        {followedLists.length === 0 ? (
          <Typography level="B2" className="text-gray-500">
            You&apos;re not following any lists yet
          </Typography>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="w-0">Creator</TableHead>
                <TableHead className="w-0">Events</TableHead>
                <TableHead className="w-0">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {followedLists.map((fl) => (
                <TableRow key={fl.id} className="group/row">
                  <TableCell>
                    <Link
                      to="/rider/$profileId/list/$listId"
                      params={{
                        profileId: fl.creatorProfileId,
                        listId: fl.listId,
                      }}
                      search={{page: 1, pageSize: 12}}
                      className="hover:underline"
                    >
                      <Typography level="B2">{fl.listName}</Typography>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Typography level="B3" className="text-gray-600">
                      {fl.creatorName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography level="B3" className="text-gray-600">
                      {fl.eventCount}
                    </Typography>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="lg:opacity-0 lg:group-hover/row:opacity-100 transition-opacity">
                      <FollowingActionsMenu listId={fl.listId} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <CreateListModal open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

/** Actions menu for a followed list row with unfollow option. */
function FollowingActionsMenu({listId}: {listId: string}) {
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  async function handleUnfollow() {
    setLoading(true);
    try {
      await unfollowEventList({data: listId});
      toastManager.add({title: 'Unfollowed list', type: 'success'});
      router.invalidate();
    } catch (err) {
      toastManager.add({
        title: err instanceof Error ? err.message : 'Failed to unfollow',
        type: 'error',
      });
      setLoading(false);
    }
  }

  return (
    <Menu>
      <MenuTrigger
        disabled={loading}
        aria-label="Open followed list actions"
        className={buttonVariants({variant: 'ghost', size: 'icon'})}
      >
        <Ellipsis className="w-4 h-4" />
      </MenuTrigger>
      <MenuPopup align="end">
        <MenuItem onClick={handleUnfollow}>Unfollow</MenuItem>
      </MenuPopup>
    </Menu>
  );
}
